import React from "react";
import { HiArrowRight } from "react-icons/hi";
import { Link } from "react-router-dom";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { useLanguage } from "../../../../context/LanguageContext";
import "./WhyReport.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Why report field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(`Why report media group "${groupKey}" is missing from the API response.`);
  }
  return resolveMediaUrl(item.url);
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    badge: requireField(f, "badge"),
    title: requireField(f, "title"),
    text: requireField(f, "text"),
    buttonText: requireField(f, "buttonText"),
    buttonUrl: requireField(f, "buttonUrl"),
    imageAlt: requireField(f, "imageAlt"),
    imageUrl: requireMediaUrl(section, "main"),
    features: [
      {
        title: requireField(f, "feature1Title"),
        text: requireField(f, "feature1Text"),
        icon: requireMediaUrl(section, "feature-1"),
      },
      {
        title: requireField(f, "feature2Title"),
        text: requireField(f, "feature2Text"),
        icon: requireMediaUrl(section, "feature-2"),
      },
      {
        title: requireField(f, "feature3Title"),
        text: requireField(f, "feature3Text"),
        icon: requireMediaUrl(section, "feature-3"),
      },
    ],
  };
};

const isExternal = (href) => /^https?:\/\//i.test(href);

const ActionLink = ({ href, className, children }) => {
  if (isExternal(href)) {
    return (
      <a href={href} className={className} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  if (href.startsWith("#") || href.includes("/#")) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
};

const WhyReport = () => {
  const { meta } = useLanguage();
  const { model, error, isLoading } = usePublicPageSection(
    "home",
    "why-report",
    buildModel
  );

  if (isLoading) {
    return (
      <section className="why-report" dir={meta.dir}>
        <div className="why-report__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="why-report" dir={meta.dir}>
        <div className="why-report__container">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Why report unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="why-report" dir={meta.dir}>
      <div className="why-report__container">
        <div className="why-report__top">
          <span className="why-report__badge">{model.badge}</span>

          <div className="why-report__content">
            <h2 className="why-report__title">{model.title}</h2>

            <p className="why-report__text">{model.text}</p>

            <ActionLink href={model.buttonUrl} className="why-report__btn">
              <span>{model.buttonText}</span>
              <HiArrowRight />
            </ActionLink>
          </div>

          <div className="why-report__features">
            {model.features.map((feature) => (
              <article key={feature.title} className="why-report__feature">
                <div className="why-report__feature-icon">
                  <img src={feature.icon} alt="" aria-hidden="true" />
                </div>
                <div className="why-report__feature-content">
                  <h3 className="why-report__feature-title">{feature.title}</h3>
                  <p className="why-report__feature-text">{feature.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <img
          src={model.imageUrl}
          alt={model.imageAlt}
          className="why-report__image"
        />
      </div>
    </section>
  );
};

export default WhyReport;
